// ============================================================================
// FILE: /frontend/src/ControllerofExammination/ResultDownloadComponent.js
// ============================================================================
// DESCRIPTION: Component for downloading student/batch results in PDF/Excel
// ============================================================================

import React, { useState } from 'react';
import api from '../api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================================
// RESULT DOWNLOAD BUTTON COMPONENT
// ============================================================================

const ResultDownloadButton = ({ 
  tenantId,
  context,
  studentId = null,
  batchId = null,
  isBatch = false,
  isPublished = true,
  buttonText = 'Download Result',
  buttonVariant = 'primary',
  className = '',
  onDownloadStart,
  onDownloadComplete,
  onDownloadError
}) => {
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [error, setError] = useState('');

  // ============================================================================
  // FETCH ORGANIZATION INFO
  // ============================================================================
  const fetchOrganizationInfo = async () => {
    try {
      const response = await api.get(`/api/organization/basic-info/${tenantId}`);
      return response.data;
    } catch (err) {
      console.warn('Failed to fetch organization info:', err);
      return null;
    }
  };

  // ============================================================================
  // FETCH LOGO AS BASE64
  // ============================================================================
  const fetchLogoAsBase64 = async (logoUrl) => {
    if (!logoUrl) return null;
    try {
      const response = await fetch(logoUrl);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn('Failed to fetch logo:', err);
      return null;
    }
  };

  // ============================================================================
  // API CALLS
  // ============================================================================
  const fetchStudentResult = async () => {
    if (!tenantId || !context || !studentId) {
      throw new Error('Missing required information');
    }

    const params = new URLSearchParams({
      tenantId,
      programTypeId: context.programTypeId,
      programId: context.programId,
      academicYearId: context.academicYearId,
      levelId: context.levelId,
      isSemesterBased: context.isSemesterBased,
    });

    if (batchId) params.append('batchId', batchId);

    const response = await api.get(`/api/coe/result-download/student/${studentId}?${params.toString()}`);
    return response.data;
  };

  const fetchBatchResult = async (formatType = 'student-wise') => {
    if (!tenantId || !context || !batchId) {
      throw new Error('Missing required information');
    }

    const params = new URLSearchParams({
      tenantId,
      programTypeId: context.programTypeId,
      programId: context.programId,
      academicYearId: context.academicYearId,
      levelId: context.levelId,
      isSemesterBased: context.isSemesterBased,
      format: formatType
    });

    const response = await api.get(`/api/coe/result-download/batch/${batchId}?${params.toString()}`);
    return response.data;
  };

  // ============================================================================
  // HELPER: Get context labels
  // ============================================================================
  const getContextLabels = (ctx) => {
    const labels = [];
    if (ctx.programTypeName) labels.push(`Program Type: ${ctx.programTypeName}`);
    if (ctx.programName) labels.push(`Program: ${ctx.programName}`);
    if (ctx.academicYearName) labels.push(`Academic Year: ${ctx.academicYearName}`);
    const levelLabel = ctx.isSemesterBased ? 'Semester' : 'Term';
    if (ctx.levelName) labels.push(`${levelLabel}: ${ctx.levelName}`);
    return labels;
  };

  // ============================================================================
  // HELPER: Split obtained and max marks safely
  // ============================================================================
  const splitMarks = (displayValue) => {
    if (!displayValue || displayValue === '-') return ['-', '-'];
    if (displayValue.includes('/')) {
      const parts = displayValue.split('/');
      return [parts[0].trim(), parts[1].trim()];
    }
    return [displayValue, '-'];
  };

  // ============================================================================
  // HELPER: Get dynamically calculated dynamic exam type string label
  // ============================================================================
  const getExamLabel = (ctx) => {
    return ctx && ctx.isSemesterBased ? 'SEE' : 'End Exam';
  };

  // ============================================================================
  // HELPER: Add header to PDF (reusable)
  // ============================================================================
  const addPDFHeader = async (doc, org, ctx, y, margin, pageWidth, showContextLabels = true, preloadedLogo = null) => {
    let logoBase64 = preloadedLogo;
    if (!logoBase64 && org.logoUrl) {
      logoBase64 = await fetchLogoAsBase64(org.logoUrl);
    }

    const logoWidth = 28;
    const logoHeight = 28;
    let currentY = y;

    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', margin, currentY + 2, logoWidth, logoHeight);
      } catch (e) {
        console.warn('PDF logo render skipped', e);
      }
    }

    const textStartX = logoBase64 ? margin + logoWidth + 8 : margin;
    const headerY = currentY + 6;

    // Institution Details
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 40, 50);
    doc.text(org.name?.toUpperCase() || 'EDUCATIONAL INSTITUTION', textStartX, headerY);

    // Metadata
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(90, 100, 110);
    doc.text(org.address || '', textStartX, headerY + 6);
    
    const contactParts = [];
    if (org.phone) contactParts.push(`Phone: ${org.phone}`);
    if (org.email) contactParts.push(`Email: ${org.email}`);
    doc.text(contactParts.join('  |  '), textStartX, headerY + 11);

    currentY = Math.max(currentY + logoHeight + 6, headerY + 16);
    doc.setDrawColor(210, 215, 220);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 5;

    // Context Labels Block
    if (showContextLabels && ctx) {
      doc.setFillColor(245, 247, 250);
      doc.rect(margin, currentY, pageWidth - (margin * 2), 16, 'F');
      
      const contextLabels = getContextLabels(ctx);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(70, 80, 90);
      
      contextLabels.forEach((label, index) => {
        const xPos = margin + 6 + (index % 2 === 0 ? 0 : 85);
        const yPos = currentY + 5.5 + (Math.floor(index / 2) * 6);
        doc.text(label, xPos, yPos);
      });
      
      currentY += 22;
    }

    return currentY;
  };

  // ============================================================================
  // HELPER: Add student info to PDF
  // ============================================================================
  const addStudentInfo = (doc, student, y, margin) => {
    doc.setFillColor(240, 244, 248);
    doc.rect(margin, y, 170, 9, 'F');
    
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 50, 60);
    doc.text(`Student Name: ${student.studentName || 'N/A'}`, margin + 4, y + 6);
    doc.text(`USN: ${student.studentUSN || 'N/A'}`, margin + 110, y + 6);
    return y + 14;
  };

  // ============================================================================
  // HELPER: Add footer to PDF
  // ============================================================================
  const addPDFFooter = (doc, pageWidth) => {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 160, 170);
      
      doc.setDrawColor(240, 242, 245);
      doc.line(20, doc.internal.pageSize.getHeight() - 14, pageWidth - 20, doc.internal.pageSize.getHeight() - 14);
      
      doc.text(`Report Card  |  Date: ${new Date().toLocaleString()}`, 20, doc.internal.pageSize.getHeight() - 9);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 35, doc.internal.pageSize.getHeight() - 9);
    }
  };

  // ============================================================================
  // PDF - Single Student
  // ============================================================================
  const generateStudentPDF = async (data) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = margin;

    const resultData = data.data || data;
    const ctx = resultData.context || {};
    const student = resultData.student || {};
    const subjects = resultData.subjects || [];
    const grandTotals = resultData.grandTotals || {};
    const summary = resultData.summary || {};

    const orgInfo = await fetchOrganizationInfo();
    const org = orgInfo || resultData.organization || {};
    
    const examLabel = getExamLabel(ctx);

    y = await addPDFHeader(doc, org, ctx, y, margin, pageWidth, true);
    y = addStudentInfo(doc, student, y, margin);

    const tableBody = subjects.map((sub, index) => {
      const [cieObtained, cieMax] = splitMarks(sub.cie?.display);
      const [endObtained, endMax] = splitMarks(sub.endExam?.display);
      const [totalObtained, totalMax] = splitMarks(sub.overall?.display);
      return [
        (index + 1).toString(),
        sub.subjectName || 'N/A',
        sub.subjectCode || 'N/A',
        cieObtained,
        cieMax,
        endObtained,
        endMax,
        totalObtained,
        totalMax
      ];
    });

    if (subjects.length === 0) {
      tableBody.push(['', 'No course records found', '', '', '', '', '', '', '']);
    }

    tableBody.push([
      '', '', '', '', '', '',
      'Grand Total', 
      (grandTotals.totalMarks || 0).toString(), 
      (grandTotals.totalMaxMarks || 0).toString()
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Sl No', 'Subject Name', 'Subject Code', 'CIE Obt', 'CIE Max', `${examLabel} Obt`, `${examLabel} Max`, 'Total Obt', 'Total Max']],
      body: tableBody,
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 3, font: 'helvetica', lineColor: [220, 225, 230] },
      headStyles: { fillColor: [50, 65, 80], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 48 },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 14, halign: 'center' },
        5: { cellWidth: 14, halign: 'center' },
        6: { cellWidth: 14, halign: 'center' },
        7: { cellWidth: 16, halign: 'center' },
        8: { cellWidth: 16, halign: 'center' }
      }
    });

    y = doc.lastAutoTable.finalY + 8;

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(220, 225, 230);
    doc.rect(margin, y, pageWidth - (margin * 2), 22, 'FD');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 70, 80);
    doc.text(`Total Obtained Marks: ${grandTotals.totalMarks || 0}`, margin + 5, y + 6);
    doc.text(`Total Max Marks: ${grandTotals.totalMaxMarks || 0}`, margin + 65, y + 6);
    doc.text(`Percentage: ${grandTotals.totalPercentage || 0}%`, margin + 125, y + 6);

    doc.setFont('helvetica', 'normal');
    doc.text(`Passed Subjects: ${grandTotals.passedSubjects || 0}`, margin + 5, y + 14);
    doc.text(`Failed Subjects: ${grandTotals.failedSubjects || 0}`, margin + 65, y + 14);
    doc.text(`Result Status: ${summary.decisionDisplay || 'N/A'}`, margin + 125, y + 14);
    
    y += 28;

    if (summary.backlogs && summary.backlogs.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(200, 50, 50);
      doc.text('Backlog Information:', margin, y);
      y += 4;

      const backlogBody = summary.backlogs.map((b, i) => [
        (i + 1).toString(),
        b.subjectName,
        b.subjectCode,
        `${b.percentage || 0}%`
      ]);

      autoTable(doc, {
        startY: y,
        head: [['Sl No', 'Subject Title', 'Code', 'Percentage']],
        body: backlogBody,
        theme: 'striped',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [220, 70, 70] }
      });
    }

    addPDFFooter(doc, pageWidth);
    return doc;
  };

  // ============================================================================
  // PDF - Batch
  // ============================================================================
  const generateBatchPDF = async (data) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = margin;

    const batchData = data.data || data;
    const ctx = batchData.batch || {};
    const summary = batchData.summary || {};
    const allSubjects = batchData.allSubjects || [];
    const students = batchData.students || [];

    const orgInfo = await fetchOrganizationInfo();
    const org = orgInfo || batchData.organization || {};
    const preloadedLogo64 = org.logoUrl ? await fetchLogoAsBase64(org.logoUrl) : null;
    
    const examLabel = getExamLabel(ctx);

    // PAGE 1: Overview Summary
    y = await addPDFHeader(doc, org, ctx, y, margin, pageWidth, true, preloadedLogo64);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 50, 60);
    doc.text('Batch Performance Overview', margin, y);
    y += 6;
    
    const summaryTable = [
      ['Total Evaluated Students', (summary.total || 0).toString(), 'Promoted Students', (summary.promoted || 0).toString()],
      ['Promoted With Backlogs', (summary.promotedWithBacklog || 0).toString(), 'Year Back Status', (summary.yearBack || 0).toString()],
      ['Not Evaluated Status', (summary.notEvaluated || 0).toString(), '', '']
    ];

    autoTable(doc, {
      startY: y,
      body: summaryTable,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 4, fontStyle: 'bold', textColor: [60, 70, 80] },
      columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 35 }, 2: { cellWidth: 50 }, 3: { cellWidth: 35 } }
    });

    y = doc.lastAutoTable.finalY + 10;

    doc.setFont('helvetica', 'bold');
    doc.text(`Mapped Subjects Course List (${allSubjects.length})`, margin, y);
    y += 5;

    const subjectsBody = allSubjects.map((s, idx) => [(idx + 1).toString(), s.subjectCode || '-', s.subjectName || '-']);
    autoTable(doc, {
      startY: y,
      head: [['Sl No', 'Course Code', 'Course Title']],
      body: subjectsBody,
      theme: 'striped',
      headStyles: { fillColor: [110, 120, 130] },
      styles: { fontSize: 8.5 }
    });

    // PAGE 2+: Individual Student Pages
    for (let idx = 0; idx < students.length; idx++) {
      const student = students[idx];
      doc.addPage();
      y = margin;

      y = await addPDFHeader(doc, org, ctx, y, margin, pageWidth, true, preloadedLogo64);
      y = addStudentInfo(doc, student, y, margin);

      const subj = student.subjects || [];
      const grandTotals = student.grandTotals || {};

      const tableBody = subj.map((sub, index) => {
        const [cieObtained, cieMax] = splitMarks(sub.cie?.display);
        const [endObtained, endMax] = splitMarks(sub.endExam?.display);
        const [totalObtained, totalMax] = splitMarks(sub.overall?.display);
        return [
          (index + 1).toString(),
          sub.subjectName || 'N/A',
          sub.subjectCode || 'N/A',
          cieObtained,
          cieMax,
          endObtained,
          endMax,
          totalObtained,
          totalMax
        ];
      });

      if (subj.length === 0) {
        tableBody.push(['', 'No course records found', '', '', '', '', '', '', '']);
      }

      tableBody.push([
        '', '', '', '', '', '',
        'Grand Total', 
        (grandTotals.totalMarks || 0).toString(), 
        (grandTotals.totalMaxMarks || 0).toString()
      ]);

      autoTable(doc, {
        startY: y,
        head: [['Sl No', 'Subject Name', 'Subject Code', 'CIE Obt', 'CIE Max', `${examLabel} Obt`, `${examLabel} Max`, 'Total Obt', 'Total Max']],
        body: tableBody,
        theme: 'grid',
        styles: { fontSize: 7.5, cellPadding: 3 },
        headStyles: { fillColor: [50, 65, 80] },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 48 },
          2: { cellWidth: 22, halign: 'center' },
          3: { cellWidth: 14, halign: 'center' },
          4: { cellWidth: 14, halign: 'center' },
          5: { cellWidth: 14, halign: 'center' },
          6: { cellWidth: 14, halign: 'center' },
          7: { cellWidth: 16, halign: 'center' },
          8: { cellWidth: 16, halign: 'center' }
        }
      });

      y = doc.lastAutoTable.finalY + 6;

      doc.setFillColor(248, 250, 252);
      doc.rect(margin, y, pageWidth - (margin * 2), 15, 'F');
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 70, 80);
      doc.text(`Obtained Marks: ${grandTotals.totalMarks || 0}   Max Marks: ${grandTotals.totalMaxMarks || 0}   Percentage: ${grandTotals.totalPercentage || 0}%`, margin + 4, y + 6);
      doc.text(`Status: ${student.decisionDisplay || 'N/A'}   Pass Count: ${grandTotals.passedSubjects || 0}   Fail Count: ${grandTotals.failedSubjects || 0}`, margin + 4, y + 11);

      if (student.backlogs && student.backlogs.length > 0) {
        y += 20;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(200, 50, 50);
        doc.text('Identified Backlogs:', margin, y);
        const bText = student.backlogs.map(b => `${b.subjectName} (${b.subjectCode})`).join(', ');
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(90, 100, 110);
        doc.text(doc.splitTextToSize(bText, pageWidth - 45), margin, y + 5);
      }
    }

    addPDFFooter(doc, pageWidth);
    return doc;
  };

  // ============================================================================
  // HELPER: Add header to Excel
  // ============================================================================
  const addExcelHeader = (rows, org, ctx) => {
    rows.push(['Institution:', org.name?.toUpperCase() || 'EDUCATIONAL INSTITUTION']);
    rows.push(['Address:', org.address || '']);
    rows.push(['Contact:', `Phone: ${org.phone || '-'} | Email: ${org.email || '-'}`]);
    rows.push([]);
    
    const contextLabels = getContextLabels(ctx);
    contextLabels.forEach(label => rows.push(['Configuration:', label]));
    
    rows.push([]);
    return rows;
  };

  // ============================================================================
  // EXCEL - Student Wise
  // ============================================================================
  const generateExcelStudentWise = (data) => {
    const workbook = XLSX.utils.book_new();
    const resultData = data.data || data;
    const org = resultData.organization || {};
    const ctx = resultData.context || resultData.batch || {};

    let students = [];
    let isSingleStudent = false;

    if (resultData.student) {
      isSingleStudent = true;
      students = [{
        studentName: resultData.student?.studentName || 'N/A',
        studentUSN: resultData.student?.studentUSN || 'N/A',
        subjects: resultData.subjects || [],
        grandTotals: resultData.grandTotals || {},
        decisionDisplay: resultData.summary?.decisionDisplay || 'N/A',
        backlogs: resultData.summary?.backlogs || []
      }];
    } else if (resultData.students) {
      students = resultData.students;
    }

    const examLabel = getExamLabel(ctx);

    const rows = [];
    addExcelHeader(rows, org, ctx);

    students.forEach((student, studentIdx) => {
      if (studentIdx > 0) {
        rows.push([]);
        rows.push([]);
      }

      const subjects = student.subjects || [];
      const grandTotals = student.grandTotals || {};

      rows.push([`Student Name: ${student.studentName}`, `USN: ${student.studentUSN}`]);
      rows.push(['Sl No', 'Subject Title', 'Subject Code', 'CIE Obtained', 'CIE Max', `${examLabel} Obtained`, `${examLabel} Max`, 'Total Obtained', 'Total Max']);

      if (subjects && subjects.length > 0) {
        subjects.forEach((sub, index) => {
          const [cieObt, cieMax] = splitMarks(sub.cie?.display);
          const [seeObt, seeMax] = splitMarks(sub.endExam?.display);
          const [totalObt, totalMax] = splitMarks(sub.overall?.display);
          rows.push([
            (index + 1).toString(),
            sub.subjectName || 'N/A',
            sub.subjectCode || 'N/A',
            cieObt,
            cieMax,
            seeObt,
            seeMax,
            totalObt,
            totalMax
          ]);
        });
      }

      rows.push(['', '', '', '', '', '', 'Grand Total:', (grandTotals.totalMarks || 0).toString(), (grandTotals.totalMaxMarks || 0).toString()]);
      rows.push([`Obtained Marks: ${grandTotals.totalMarks || 0}`, `Max Marks: ${grandTotals.totalMaxMarks || 0}`, `Percentage: ${grandTotals.totalPercentage || 0}%`, `Passed Count: ${grandTotals.passedSubjects || 0}`, `Failed Count: ${grandTotals.failedSubjects || 0}`, `Status Option: ${student.decisionDisplay}`]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, isSingleStudent ? 'Student Grade sheet' : 'Cohort Result sheet');
    worksheet['!cols'] = [{ wch: 8 }, { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];

    return workbook;
  };

  // ============================================================================
  // EXCEL - Subject Wise Analysis
  // ============================================================================
  const generateExcelSubjectWise = (data) => {
    const workbook = XLSX.utils.book_new();
    const batchData = data.data || data;
    const org = batchData.organization || {};
    const ctx = batchData.batch || {};
    const allSubjects = batchData.allSubjects || [];
    const students = batchData.students || [];
    
    const examLabel = getExamLabel(ctx);

    const overallRows = [];
    addExcelHeader(overallRows, org, ctx);
    overallRows.push(['COHORT SUBJECT-WISE METRICS ANALYSIS']);
    overallRows.push([]);

    let headerRow = ['Sl No', 'Student Name', 'USN Number'];
    allSubjects.forEach((subject) => {
      headerRow.push(`${subject.subjectName} (${subject.subjectCode}) Obtained Marks`);
    });
    headerRow.push('Final Total Marks', 'Passed Subjects', 'Failed Subjects', 'Status Summary');
    overallRows.push(headerRow);

    students.forEach((student, idx) => {
      const row = [(idx + 1).toString(), student.studentName || 'N/A', student.studentUSN || 'N/A'];
      const subjects = student.subjects || [];
      const grandTotals = student.grandTotals || {};

      allSubjects.forEach((subject) => {
        const studentSubject = subjects.find(s => s.subjectId?.toString() === subject.subjectId?.toString());
        const [obtained] = splitMarks(studentSubject?.overall?.display);
        row.push(obtained);
      });

      row.push(grandTotals.totalMarks || 0, grandTotals.passedSubjects || 0, grandTotals.failedSubjects || 0, student.decisionDisplay || 'N/A');
      overallRows.push(row);
    });

    const overallSheet = XLSX.utils.aoa_to_sheet(overallRows);
    XLSX.utils.book_append_sheet(workbook, overallSheet, 'Overall Class Analysis');

    allSubjects.forEach((subject) => {
      const sheetRows = [];
      addExcelHeader(sheetRows, org, ctx);
      sheetRows.push([`Subject Course Analysis: ${subject.subjectName} (${subject.subjectCode})`]);
      sheetRows.push([]);
      sheetRows.push(['Sl No', 'Student Name', 'USN', 'CIE Obtained', 'CIE Max', `${examLabel} Obtained`, `${examLabel} Max`, 'Final Obtained', 'Final Max', 'Status Outcome']);

      students.forEach((student, idx) => {
        const studentSubject = student.subjects?.find(s => s.subjectId?.toString() === subject.subjectId?.toString());
        const [cieObt, cieMax] = splitMarks(studentSubject?.cie?.display);
        const [seeObt, seeMax] = splitMarks(studentSubject?.endExam?.display);
        const [finalObt, finalMax] = splitMarks(studentSubject?.overall?.display);

        sheetRows.push([
          (idx + 1).toString(),
          student.studentName || 'N/A',
          student.studentUSN || 'N/A',
          cieObt,
          cieMax,
          seeObt,
          seeMax,
          finalObt,
          finalMax,
          studentSubject?.isPassed ? 'PASS' : 'FAIL'
        ]);
      });

      const passedCount = students.filter(s => {
        const ss = s.subjects?.find(sb => sb.subjectId?.toString() === subject.subjectId?.toString());
        return ss?.isPassed;
      }).length;

      sheetRows.push([]);
      sheetRows.push(['Class Metrics Summary', `Total Student Strength: ${students.length}`, `Total Passed: ${passedCount}`, `Total Failed: ${students.length - passedCount}`]);

      const sheetName = subject.subjectName.length > 22 ? `${subject.subjectName.substring(0, 22)}..` : subject.subjectName;
      const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    workbook.SheetNames.forEach(name => {
      const sheet = workbook.Sheets[name];
      if (sheet) sheet['!cols'] = Array.from({ length: 24 }, () => ({ wch: 18 }));
    });

    return workbook;
  };

  // ============================================================================
  // DOWNLOAD HANDLER
  // ============================================================================
  const handleDownload = async (format, type = null) => {
    if (!isPublished) {
      setError('Results must be published before downloading');
      return;
    }

    setLoading(true);
    setError('');
    setShowMenu(false);

    try {
      if (onDownloadStart) onDownloadStart();

      let result;
      let filename;

      if (isBatch) {
        const formatType = type === 'subject-wise' ? 'subject-wise' : 'student-wise';
        result = await fetchBatchResult(formatType);
        filename = `batch_${batchId}`;
      } else {
        result = await fetchStudentResult();
        filename = `student_${studentId}`;
      }

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch data');
      }

      if (format === 'pdf') {
        let doc;
        if (isBatch) {
          doc = await generateBatchPDF(result);
        } else {
          doc = await generateStudentPDF(result);
        }
        doc.save(`${filename}_result.pdf`);
      } else if (format === 'excel') {
        let workbook;
        if (type === 'subject-wise' && isBatch) {
          workbook = generateExcelSubjectWise(result);
          filename = `${filename}_subject_wise`;
        } else {
          workbook = generateExcelStudentWise(result);
          filename = `${filename}_student_wise`;
        }
        XLSX.writeFile(workbook, `${filename}_result.xlsx`);
      }

      if (onDownloadComplete) onDownloadComplete();

    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Download failed');
      if (onDownloadError) onDownloadError(err);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // RENDER UI BLOCKS (MOBILE FIRST IMPLEMENTATION)
  // ============================================================================

  const variants = {
    primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
    secondary: 'bg-slate-200 hover:bg-slate-300 text-slate-800',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    danger: 'bg-rose-600 hover:bg-rose-700 text-white',
    outline: 'border border-slate-300 bg-white hover:bg-slate-50 text-slate-700'
  };

  if (!isPublished) {
    return (
      <button
        disabled={true}
        className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium bg-slate-100 text-slate-400 cursor-not-allowed ${className}`}
        title="Results must be published before downloading"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        <span>{buttonText}</span>
      </button>
    );
  }

  return (
    <div className="relative inline-block w-full sm:w-auto text-left">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={loading}
        className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 ${variants[buttonVariant] || variants.primary} ${className} disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {loading ? (
          <>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            <span>Processing Data...</span>
          </>
        ) : (
          <>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>{buttonText}</span>
            <svg className={`h-4 w-4 transition-transform duration-200 ${showMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {showMenu && (
        <div className="absolute right-0 left-0 sm:left-auto mt-2 w-full sm:w-64 rounded-xl border border-slate-200 bg-white shadow-xl z-50 overflow-hidden transform origin-top-right transition-all duration-200">
          <div className="p-1 space-y-0.5">
            <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Official PDF Report
            </div>
            <button
              onClick={() => handleDownload('pdf')}
              className="w-full rounded-lg px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-indigo-50 transition-colors flex items-center gap-2"
            >
              <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{isBatch ? 'Complete Batch Ledger' : 'Student Grade Sheet'}</span>
            </button>

            <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider border-t border-slate-100 mt-1 pt-2">
              Excel Spreadsheet
            </div>
            <button
              onClick={() => handleDownload('excel', 'student-wise')}
              className="w-full rounded-lg px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-indigo-50 transition-colors flex items-center gap-2"
            >
              <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Student Performance View</span>
            </button>
            {isBatch && (
              <button
                onClick={() => handleDownload('excel', 'subject-wise')}
                className="w-full rounded-lg px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-indigo-50 transition-colors flex items-center gap-2"
              >
                <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>Subject Matrix Analysis</span>
              </button>
            )}

            {error && (
              <div className="px-3 py-2 text-xs text-rose-600 border-t border-rose-100 mt-1 bg-rose-50 rounded-b-lg">
                Warning: {error}
              </div>
            )}
          </div>
        </div>
      )}

      {showMenu && (
        <div 
          className="fixed inset-0 z-40 bg-black/5 sm:bg-transparent"
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
};

export default ResultDownloadButton;